import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PlaceholderTab } from "@/components/consultation/tabs/PlaceholderTab";
import { FamilyHistoryCard } from "@/components/consultation/cards/FamilyHistoryCard";
import { PatientConcernCard } from "@/components/consultation/cards/PatientConcernCard";
import type {
  FamilyDiseaseHistory,
  PatientConcern,
} from "@/types/appointments";

import { PrescriptionObservations } from "@/components/consultation/tabs/PrescriptionObservations";
import { MedicineCard } from "@/components/consultation/cards/MedicineCard";
import { InvestigationAdviceCard } from "@/components/consultation/cards/InvestigationAdviceCard";
import { MonitoringAdviceCard } from "@/components/consultation/tabs/monitoring/MonitoringAdviceCard";
import { GeneralAdviceCard } from "@/components/consultation/tabs/monitoring/GeneralAdviceCard";
import { FollowupReferralTab } from "@/components/consultation/tabs/followup/FollowupReferralTab";

interface PrescriptionsHomeProps {
  familyHistory: FamilyDiseaseHistory[];
  patientConcerns: PatientConcern[];
  activeAppointmentId?: string;
  patientId?: string;
  onManageFatherHistory: () => void;
  onManageMotherHistory: () => void;
  onManageSiblingHistory: () => void;
  onManageConcern: () => void;
  isCompleted?: boolean;
  prescriptionUrl?: string | null;
}

type PrescriptionStep =
  | "patient-info"
  | "observations"
  | "prescriptions"
  | "monitoring"
  | "follow-up";

export const PrescriptionsHome = ({
  familyHistory,
  patientConcerns,
  activeAppointmentId,
  patientId,
  onManageFatherHistory,
  onManageMotherHistory,
  onManageSiblingHistory,
  onManageConcern,
  isCompleted = false,
  prescriptionUrl,
}: PrescriptionsHomeProps) => {
  const [activeStep, setActiveStep] =
    useState<PrescriptionStep>("patient-info");

  const steps: { id: PrescriptionStep; label: string }[] = [
    { id: "patient-info", label: "History & Concerns" },
    { id: "observations", label: "Observations" },
    { id: "prescriptions", label: "Rx & Investigations" },
    { id: "monitoring", label: "Monitoring & Advice" },
    { id: "follow-up", label: "Follow-up & Referral" },
  ];

  // We need to override the BrowserWindow tabs/header if we want this exact design.
  // Or we can render this navigation INSIDE the BrowserWindow content area,
  // assuming the parent Consultation component handles the main "Prescriptions" tab active state.

  // Based on "tabs in the in-window browser card would be fixed", it implies this navigation replaces the dynamic tabs.
  // Since BrowserWindow renders `children`, we can put the navigation here.
  // Ideally, BrowserWindow should accept a prop to hide its own tabs or we just render below it.
  // But the user said "tabs in the in-window browser card would be fixed".
  // This suggests we might need to push this navigation UP to where BrowserWindow tabs are, OR
  // just render a secondary navigation here if BrowserWindow tabs are hidden/fixed.

  // For now, I will render the navigation bar here at the top of the content.
  // If the user meant REPLACING the top bar of BrowserWindow, I might need to adjust Consultation.tsx.

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Stepper/Tabs Navigation */}
      <div className="bg-card border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin p-1 bg-muted/50 rounded-lg">
          {steps.map((step, index) => (
            <Button
              key={step.id}
              variant="ghost"
              onClick={() => setActiveStep(step.id)}
              className={cn(
                "flex-1 min-w-fit h-9 rounded-xl z-caption transition-all",
                activeStep === step.id
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/70"
              )}
            >
              <span className="mr-2 opacity-50 z-hint">{index + 1}.</span>
              {step.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/20 p-4">
        {activeStep === "patient-info" && (
          <div className="space-y-6">
            <FamilyHistoryCard
              familyHistory={familyHistory}
              onManageFatherHistory={onManageFatherHistory}
              onManageMotherHistory={onManageMotherHistory}
              onManageSiblingHistory={onManageSiblingHistory}
              isCompleted={isCompleted}
            />
            <PatientConcernCard
              patientConcerns={patientConcerns}
              activeAppointmentId={activeAppointmentId}
              onManageConcern={onManageConcern}
              variant="default" // Use default (larger) variant for main content
              isCompleted={isCompleted}
            />
          </div>
        )}

        {activeStep === "observations" && activeAppointmentId && patientId && (
          <PrescriptionObservations
            appointmentId={activeAppointmentId}
            patientId={patientId}
            isCompleted={isCompleted}
          />
        )}

        {activeStep === "prescriptions" && activeAppointmentId && (
          <div className="space-y-6">
            <MedicineCard
              appointmentId={activeAppointmentId}
              isCompleted={isCompleted}
            />
            <InvestigationAdviceCard
              appointmentId={activeAppointmentId}
              isCompleted={isCompleted}
            />
          </div>
        )}

        {activeStep === "monitoring" && activeAppointmentId && (
          <div className="space-y-6">
            <MonitoringAdviceCard
              appointmentId={activeAppointmentId}
              isCompleted={isCompleted}
            />
            <GeneralAdviceCard
              appointmentId={activeAppointmentId}
              isCompleted={isCompleted}
            />
          </div>
        )}

        {activeStep === "follow-up" && activeAppointmentId && (
          <FollowupReferralTab
            appointmentId={activeAppointmentId}
            isCompleted={isCompleted}
            prescriptionUrl={prescriptionUrl}
          />
        )}

        {activeStep !== "patient-info" &&
          activeStep !== "observations" &&
          activeStep !== "prescriptions" &&
          activeStep !== "monitoring" &&
          activeStep !== "follow-up" && (
            <PlaceholderTab
              title={steps.find((s) => s.id === activeStep)?.label || "Step"}
            />
          )}
      </div>
    </div>
  );
};
